import type { CampaignDetailCampaign } from "@adpilot/shared";
import { type ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { PageTabs } from "@/components/patterns/PageTabs";
import { useFormatters } from "@/lib/format";
import { CampaignBiddingStrategyCell } from "./CampaignBiddingStrategyCell";
import { CampaignStatusBadge } from "./CampaignStatusBadge";
import { CampaignTypeBadge } from "./CampaignTypeBadge";

interface CampaignDetailHeadingProps {
  campaign: CampaignDetailCampaign;
  actions?: ReactNode;
  filters?: ReactNode;
  breadcrumb?: ReactNode;
}

export function CampaignDetailHeading({
  campaign,
  actions,
  filters,
  breadcrumb,
}: CampaignDetailHeadingProps) {
  const { t } = useTranslation("campaign");
  const { formatCurrency } = useFormatters();
  return (
    <header className="flex min-w-0 flex-col gap-3">
      {breadcrumb}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <span className="flex h-6 shrink-0 items-center">
            <CampaignTypeBadge campaignType={campaign.campaignType} />
          </span>
          <h1 className="min-w-0 break-words text-[18px] font-semibold leading-6 tracking-[-0.02em] text-ink [overflow-wrap:anywhere]">
            {campaign.name}
          </h1>
        </div>
        <div className="flex max-w-full flex-col items-end gap-3">
          {filters}
          <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] leading-4 text-ink">
        <CampaignStatusBadge status={campaign.status} />
        <dl className="flex flex-wrap gap-x-5 gap-y-1.5">
          <div className="flex items-center gap-1.5">
            <dt className="text-muted-foreground">{t("detail.shell.budget")}</dt>
            <dd className="font-medium tabular-nums">
              {campaign.dailyBudget === null
                ? "—"
                : formatCurrency(campaign.dailyBudget, campaign.budgetHealth.currency ?? "USD")}
              <span className="ml-1 font-normal text-muted-foreground">
                /{" "}
                {t(
                  campaign.budgetType === "monthly" ? "detail.shell.monthly" : "detail.shell.daily",
                )}
              </span>
            </dd>
          </div>
          <div className="flex items-center gap-1.5">
            <dt className="text-muted-foreground">{t("detail.shell.targeting")}</dt>
            <dd>{campaign.targetingType ? t(`detail.shell.${campaign.targetingType}`) : "—"}</dd>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <dt className="text-muted-foreground">{t("detail.shell.schedule")}</dt>
            <dd className="tabular-nums">
              {campaign.startDate ? (
                <time dateTime={campaign.startDate}>{campaign.startDate}</time>
              ) : (
                "—"
              )}
              {campaign.startDate || campaign.endDate ? (
                <>
                  {" "}
                  ~{" "}
                  {campaign.endDate ? (
                    <time dateTime={campaign.endDate}>{campaign.endDate}</time>
                  ) : (
                    t("detail.shell.noEndDate")
                  )}
                </>
              ) : null}
            </dd>
          </div>
          <div className="flex min-w-0 items-start gap-1.5">
            <dt className="shrink-0 text-muted-foreground">{t("detail.shell.portfolio")}</dt>
            <dd className="min-w-0 break-words [overflow-wrap:anywhere]">
              {campaign.portfolioName ?? "—"}
            </dd>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <dt className="text-muted-foreground">{t("detail.shell.biddingStrategy")}</dt>
            <dd>
              <CampaignBiddingStrategyCell strategy={campaign.biddingStrategy ?? null} />
            </dd>
          </div>
        </dl>
      </div>
    </header>
  );
}

interface CampaignDetailNavigationProps {
  children?: ReactNode;
  adGroupCount?: number;
  targetCount?: number;
  negativeCount?: number;
  searchTermCount?: number;
  runCount: number;
}

/** Keep the reference order, but do not present Ad Group contracts as Campaign-wide data. */
export function CampaignDetailNavigation({
  children,
  adGroupCount,
  targetCount,
  negativeCount,
  searchTermCount,
  runCount,
}: CampaignDetailNavigationProps) {
  const { t } = useTranslation("campaign");
  const id = useId();
  const tabs = [
    { value: "ad-groups", label: t("detail.tabs.adGroups"), count: adGroupCount },
    { value: "budget", label: t("detail.shell.tabs.budget") },
    { value: "automation", label: t("detail.shell.tabs.automation"), reason: "notConnected" },
    { value: "products", label: t("detail.shell.tabs.products"), reason: "unavailableAtCampaign" },
    { value: "targets", label: t("detail.tabs.targets"), count: targetCount },
    { value: "negatives", label: t("detail.tabs.negatives"), count: negativeCount },
    { value: "search-terms", label: t("detail.tabs.searchTerms"), count: searchTermCount },
    { value: "bid-adjustment", label: t("detail.shell.tabs.bidAdjustment") },
    { value: "log", label: t("detail.shell.tabs.log") },
    // Existing AdPraxis views retain their names and remain reachable during the staged rollout.
    { value: "overview", label: t("detail.tabs.overview") },
    { value: "ai-runs", label: t("detail.tabs.aiRuns"), count: runCount },
  ];
  return (
    <PageTabs
      defaultValue="ad-groups"
      density="compact"
      className="min-w-0"
      aria-label={t("detail.shell.navigation")}
      items={tabs.map((tab) => ({
        value: tab.value,
        label: tab.label,
        count: tab.count,
        disabled: !!tab.reason,
        describedBy: tab.reason ? `${id}-${tab.reason}` : undefined,
      }))}
    >
      <div className="flex flex-col gap-0.5 text-[11px] leading-4 text-muted-foreground">
        <p id={`${id}-unavailableAtCampaign`}>{t("detail.shell.unavailableAtCampaign")}</p>
        <p id={`${id}-notConnected`}>{t("detail.shell.notConnected")}</p>
      </div>
      {children}
    </PageTabs>
  );
}
